import { Dispatch, ReactNode, SetStateAction, Suspense } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  UsageTypeSwitch,
  UsageType,
  UsageTypeValue,
} from 'src/common/components/UsageTypeSwitch/UsageTypeSwitch';
import {
  ChartTypeToggle,
  ChartType,
  ChartTypeValue,
} from 'src/common/components/ChartTypeToggle/ChartTypeToggle';
import { LEGEND_BLUE_COLOR, LEGEND_GREEN_COLOR } from 'src/common/utils/mapUsageDTOtoAreaChartData';
import { GraphsColorLegend } from 'src/common/components/GraphColorsLegend/GraphColorsLegend';
import { WidgetDailyUsageIndicator } from 'src/dashboard/Componets/WidgetDailyUpdateIndicator';
import { AreaChartSkeleton } from './components/AreaChartSkeleton';
import { ErrorBoundary } from 'react-error-boundary';
import { EmptyLineOrBarChart } from './EmptyLineOrBarChart';
import { pxToRem } from 'src/common/utils/fonts';
import { parseDateAndTimeToLocal } from 'common/utils/parseResponseDate.ts';

const HEADER_HEIGHT = 32;
const HEADER_BOTTOM_MARING = 16;

const UsageTypeContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  position: 'relative',
  height: theme.spacing(HEADER_HEIGHT),
  marginBottom: theme.spacing(HEADER_BOTTOM_MARING),
}));

const LegendContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(10),
}));

export const StyledUsageChartsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: `calc(100% - ${theme.spacing(HEADER_HEIGHT)} - ${theme.spacing(HEADER_BOTTOM_MARING)})`,
  gap: theme.spacing(40),

  '@media(max-width: 900px)': {
    flexDirection: 'column',
    gap: theme.spacing(20),
  },
}));

export const StyledChartItem = styled(Box)<{ extraTopPadding?: boolean }>(
  ({ extraTopPadding, theme }) => ({
    position: 'relative',
    paddingTop: extraTopPadding ? theme.spacing(20) : '0px',
    flex: '1 1 0%',
    minWidth: 0,
  }),
);

const StyledSkeletonContainer = styled(Box)(() => ({
  height: '100%',
  paddingBottom: '1rem',
}));
const ChartTitle = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: theme.spacing(8),
}));

const ChartLegend = ({
  usageType,
  lastUpdated,
}: {
  usageType: UsageTypeValue;
  lastUpdated?: Date;
}) => {
  const legendItems = [
    {
      color: LEGEND_GREEN_COLOR,
      description: usageType === UsageType.Data ? 'Upload' : 'MO',
    },
    { color: LEGEND_BLUE_COLOR, description: usageType === UsageType.Data ? 'Download' : 'MT' },
  ];

  return (
    <LegendContainer>
      <GraphsColorLegend legendItems={legendItems} />
      <WidgetDailyUsageIndicator date={parseDateAndTimeToLocal(lastUpdated)} />
    </LegendContainer>
  );
};

const ChartSuspenseContainer = ({
  children,
  toggleConfig,
  resetKeys,
}: {
  children: ReactNode;
  toggleConfig?: { isOn: boolean; onToggle: () => void };
  resetKeys: string[];
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Suspense
        fallback={
          <StyledSkeletonContainer>
            <AreaChartSkeleton />
          </StyledSkeletonContainer>
        }
      >
        <ErrorBoundary
          resetKeys={resetKeys}
          fallback={<EmptyLineOrBarChart as={Line} emptyChartText={t('common.somethingWrong')} />}
        >
          {children}
        </ErrorBoundary>
      </Suspense>

      {toggleConfig && (
        <Box
          sx={{
            position: 'absolute',
            bottom: pxToRem(-10),
          }}
        >
          <ChartTypeToggle isOn={toggleConfig.isOn} onToggle={toggleConfig.onToggle} />
        </Box>
      )}
    </>
  );
};

type UsageSectionProps = {
  headerUsageTitle?: string;
  usageType: UsageTypeValue;
  setUsageType: Dispatch<SetStateAction<UsageTypeValue>>;
  chartType: ChartTypeValue;
  toggleChartType: () => void;
  displayMonthlyChart?: boolean;
  monthlyChart: ReactNode;
  displayDailyChart?: boolean;
  dailyChart: ReactNode;
  shouldDisplayLegend?: boolean;
  displayChartsTitle?: boolean;
  lastUpdated?: Date;
};

export const UsageCharts = ({
  headerUsageTitle,
  usageType,
  setUsageType,
  chartType,
  toggleChartType,
  displayMonthlyChart,
  monthlyChart,
  displayDailyChart,
  dailyChart,
  shouldDisplayLegend,
  displayChartsTitle,
  lastUpdated,
}: UsageSectionProps) => {
  const { t } = useTranslation();

  return (
    <>
      <UsageTypeContainer>
        <Typography variant="h3" color="text.secondary" pl={10}>
          {headerUsageTitle}
        </Typography>
        <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)' }}>
          <UsageTypeSwitch value={usageType} onChange={setUsageType} />
        </Box>
        {shouldDisplayLegend && <ChartLegend usageType={usageType} lastUpdated={lastUpdated} />}
      </UsageTypeContainer>
      <StyledUsageChartsContainer aria-label="Monthly Usage" role="region">
        {displayMonthlyChart && (
          <StyledChartItem extraTopPadding={displayChartsTitle}>
            {displayChartsTitle && (
              <ChartTitle variant="text2" color="text.secondary">
                {t('simInventory.monthlyUsage')}
              </ChartTitle>
            )}
            <ChartSuspenseContainer resetKeys={[usageType]}>
              <Box sx={{ height: '100%' }} aria-label="Monthly Usage" role="figure">
                {monthlyChart}
              </Box>
            </ChartSuspenseContainer>
          </StyledChartItem>
        )}
        {displayDailyChart && (
          <StyledChartItem
            extraTopPadding={displayChartsTitle}
            aria-label="Daily Usage"
            role="region"
          >
            <ChartSuspenseContainer
              toggleConfig={{
                isOn: chartType === ChartType.NonCumulative,
                onToggle: toggleChartType,
              }}
              resetKeys={[usageType, chartType]}
            >
              {displayChartsTitle && (
                <ChartTitle variant="text2" color="text.secondary">
                  {t('simInventory.dailyUsage')}
                </ChartTitle>
              )}
              <Box sx={{ height: '100%' }} aria-label="Daily Usage" role="figure">
                {dailyChart}
              </Box>
            </ChartSuspenseContainer>
          </StyledChartItem>
        )}
      </StyledUsageChartsContainer>
    </>
  );
};
